import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { cn, objectToFormData, validateStep } from '@/lib/utils';
import { CacheControl } from '@/lib/s3';
import ModelUpload from './Carousel/ModelUpload';
import CarouselHeader from '@/components/FullPageCarousel/CarouselHeader';
import ProgressBar from '@/components/FullPageCarousel/ProgressBar';
import MediaUpload from './Carousel/MediaUpload';
import PostStatus from './Carousel/PostStatus';
import BasicDetails from './Carousel/BasicDetails';
import {
  useCreateDraftPostMutation,
  useLazyGetPostByIdQuery,
  useUpdatePostDataMutation,
} from '@/redux/post/post.api.slice';
import {
  jobSetupModelSchema,
  licenceDetailsSchema,
  mediaUploadSchema,
  modelToolMappingPageSchema,
  modelUploadSchema,
  postUploadSchema,
} from '@/redux/post/post.schema';
import { IPostDraft } from '@/redux/post/post.d';
import JobSetupDetails from './Carousel/JobSetupDetails';
import {
  convertToPostDetailData,
  convertToPostUploadData,
} from '@/redux/post/post.utils';
import ModelToolMapping from './Carousel/ModelToolMapping';
import useSearchParamActions from '@/customHooks/useSearchParamActions';
import Loader from './Carousel/Loader';
import LicenceDetails from './Carousel/LicenceDetails';
import { ICarouselPage } from '@/interfaces/types';
import {
  resetProjectEditorData,
  selectProjectEditorData,
  setProjectEditorData,
} from '@/redux/post/post.slice';
import { useGetPreSignedUploadUrlMutation } from '@/redux/attachment/attachment.api.slice';
import { IAttachmentCategory } from '@/redux/attachment/attachment.d';
import { selectCurrentUser } from '@/redux/auth/auth.slice';

export default function PostUpload() {
  const [currentStep, setCurrentStep] = useState(1);
  const [createDraftPost, { isLoading: isCreateDraftPostLoading }] =
    useCreateDraftPostMutation();
  const [
    updatePostData,
    { isLoading: isUpdatePostDataLoading, status: updatePostDataStatus },
  ] = useUpdatePostDataMutation();
  const [getPostByIdQuery, { isLoading: isGetPostByIdLoading }] =
    useLazyGetPostByIdQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const { postId } = useParams();
  const { getSearchParam, appendSearchParam } = useSearchParamActions();
  const dispatch = useDispatch();
  const projectEditorData = useSelector(selectProjectEditorData);
  const currentUser = useSelector(selectCurrentUser);
  const isEditMode = getSearchParam('edit') === 'true';
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [getPreSignedUploadUrl] = useGetPreSignedUploadUrlMutation();
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const isUploadingFilesRef = useRef(false);

  useEffect(() => {
    // Only reset if we're creating a new post, not editing an existing one
    if (!postId) {
      dispatch(resetProjectEditorData());
    }
  }, [dispatch, postId]);

  async function handleCreateDraftPost(data: Partial<IPostDraft>) {
    try {
      const thumbnailFile = new File(
        [await fetch(data.thumbnail?.preview || '').then((res) => res.blob())],
        data.thumbnail?.fileName || 'thumbnail.jpg'
      );

      const postFormData = objectToFormData({
        ...data,
        thumbnail: thumbnailFile,
      });

      const createDraftPostRes = await createDraftPost(postFormData).unwrap();
      dispatch(
        setProjectEditorData({
          ...projectEditorData,
          fileUploadQueue: [],
        })
      );
      navigate(`/project/edit/${createDraftPostRes._id}?page=2`);
      toast.success('Model Post Upload successful');
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'data' in error
          ? (error as { data?: { errorMessage?: string } }).data?.errorMessage
          : 'Something went wrong';
      toast.error('Project creation failed', {
        description: errorMessage,
      });
    }
  }

  let pages: ICarouselPage[] = [];

  async function uploadFileFromUrlToPresignedUrl(
    fileObjectUrl: string,
    presignedUrl: string,
    contentType = ''
  ) {
    try {
      const fetchResponse = await fetch(fileObjectUrl);

      if (!fetchResponse.ok) {
        const fetchErrorText = await fetchResponse.text();
        throw new Error(
          `Failed to fetch file from ${fileObjectUrl}. Status: ${fetchResponse.status}, ${fetchResponse.statusText}. Details: ${fetchErrorText}`
        );
      }

      const fileContentBlob = await fetchResponse.blob();

      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: fileContentBlob,
        headers: {
          'Content-Type': contentType,
          'Cache-Control': CacheControl.IMMUTABLE,
        },
      });

      if (uploadResponse.ok) {
        return {
          status: true,
          message: 'File uploaded successfully',
        };
      }
      throw new Error(
        `Failed to upload file. Status: ${uploadResponse.status}, ${uploadResponse.statusText}`
      );
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Unknown error occurred';
      return {
        status: false,
        message,
      };
    }
  }

  const uploadQueuedFiles = async () => {
    if (isUploadingFilesRef.current) return;
    if (projectEditorData.fileUploadQueue.length === 0) return;

    // Snapshot the queue and clear it immediately so any concurrent caller
    // (Save / Next / step-click) can't re-send the same files.
    const queueSnapshot = projectEditorData.fileUploadQueue;
    isUploadingFilesRef.current = true;
    setIsUploadingFiles(true);
    dispatch(
      setProjectEditorData({
        ...projectEditorData,
        fileUploadQueue: [],
      })
    );

    try {
      const attachments = await getPreSignedUploadUrl({
        attachments: queueSnapshot.map((file) => ({
          parentId: file.parentId,
          parentType: file.parentType,
          category: file.category,
          fileName: file.fileName,
          fileHash: file.fileHash,
          fileSize: file.fileSize,
          resourceType: file.resourceType,
        })),
      }).unwrap();

      if (attachments.length === 0) return;

      const signedUploadUrlsMap = new Map(
        attachments.map((url) => [url.fileHash, url.signedUrl])
      );

      const uploadFilePromises = queueSnapshot.map((file) => {
        const signedUrl = signedUploadUrlsMap.get(file.fileHash);
        if (!signedUrl) return Promise.resolve();
        return uploadFileFromUrlToPresignedUrl(file.preview, signedUrl);
      });

      await Promise.all(uploadFilePromises);

      // Update models with newly uploaded files
      const newModels = attachments
        .filter((attachment) => attachment.attachmentId)
        .map((attachment) => {
          const originalFile = queueSnapshot.find(
            (file) => file.fileHash === attachment.fileHash
          );
          if (!originalFile) return null;

          return {
            _id: attachment.attachmentId,
            fileName: originalFile.fileName,
            fileSize: originalFile.fileSize,
            fileHash: originalFile.fileHash,
            category: originalFile.category,
            resourceType: originalFile.resourceType,
            parentId: originalFile.parentId,
            parentType: originalFile.parentType,
            user: '',
            resourcePath: '',
            url: originalFile.preview,
            downloadCount: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        })
        .filter(Boolean);

      dispatch(
        setProjectEditorData({
          ...projectEditorData,
          models: [...projectEditorData.models, ...newModels],
          fileUploadQueue: [],
        })
      );

      // Refresh post data to get proper URLs for uploaded files
      if (postId) {
        const { data: updatedPostData } = await getPostByIdQuery(postId);
        if (updatedPostData) {
          const postFormData = await convertToPostUploadData(updatedPostData);
          dispatch(setProjectEditorData({ ...postFormData, isLoading: false }));
        }
      }
    } finally {
      isUploadingFilesRef.current = false;
      setIsUploadingFiles(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!postId) return;

    setIsSaving(true);
    try {
      await updatePostData({
        postId,
        data: {
          ...convertToPostDetailData(projectEditorData),
          currentPage: currentStep,
        },
      });
      await uploadQueuedFiles();
      toast.success('Changes saved successfully');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // The ONLY action that submits the project for approval. Triggered by the
  // Publish button on the final input step (License Details) — there is no
  // separate publish screen. Sends publish:true so the backend transitions
  // visibility and emails once; a single Publish action covers both first-time
  // publish (draft) and re-submitting an edited live project — the backend
  // decides based on the project's current visibility. On success we advance to
  // the Post Status step, which shows the existing submission confirmation.
  const handlePublish = async () => {
    if (!postId) return;

    setIsPublishing(true);
    try {
      // Mirror the other save handlers: persist (here with publish:true) first,
      // then flush any pending file uploads.
      await updatePostData({
        postId,
        data: {
          ...convertToPostDetailData(projectEditorData),
          currentPage: currentStep,
          publish: true,
        },
      }).unwrap();
      await uploadQueuedFiles();
      toast.success('Project submitted for approval');
      setCurrentStep(pages.length);
    } catch (error) {
      toast.error('Failed to submit project for approval. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      const previousStep = currentStep - 1;
      setCurrentStep(previousStep);
      appendSearchParam('page', String(previousStep), true);
    } else {
      navigate('/');
    }
  };

  const getCurrentPageInfo = () => {
    const currentPage = pages[currentStep - 1];
    if (!currentPage) return { title: '', description: '' };

    const descriptions: { [key: string]: string } = {
      'Basic Details': 'Enter basic details about your project',
      'Upload Gcode': 'Upload your Gcode files',
      'Assign Tools to Gcode': 'Assign tools to your Gcode files',
      'CAD/Project File Upload': 'Upload media files for your project',
      'Job Setup Details': 'Configure Project Setup Parameters',
      'License Details': 'Set up licensing information',
      'Post Status': 'Your project submission status',
    };

    return {
      title: currentPage.title || '',
      description: descriptions[currentPage.title || ''] || '',
    };
  };

  pages = [
    {
      page: <BasicDetails errors={errors} key="basic-details" />,
      schema: postUploadSchema,
      condition: true,
      title: 'Basic Details',
    },
    {
      page: <ModelUpload errors={errors} key="model-upload" />,
      schema: modelUploadSchema,
      condition: true,
      title: 'Upload Gcode',
    },
    {
      page: <ModelToolMapping pageErrors={errors} key="onefinity-details" />,
      schema: modelToolMappingPageSchema,
      condition: true,
      title: 'Assign Tools to Gcode',
    },
    {
      page: <MediaUpload errors={errors} key="media-upload" />,
      schema: mediaUploadSchema,
      condition: true,
      title: 'CAD/Project File Upload',
    },
    {
      page: <JobSetupDetails errors={errors} key="job-setup-details" />,
      schema: jobSetupModelSchema,
      condition: true,
      title: 'Job Setup Details',
    },
    {
      page: <LicenceDetails errors={errors} key="licence-details" />,
      schema: licenceDetailsSchema,
      condition: true,
      title: 'License Details',
    },
    {
      page: (
        <PostStatus key="post-status" isLoading={isCreateDraftPostLoading} />
      ),
      title: 'Post Status',
    },
  ];

  const handleNext = async () => {
    const nextPage = pages[currentStep];
    const validationResult = await validateStep(
      currentStep,
      pages as ICarouselPage[],
      projectEditorData,
      setErrors
    );
    // const validationResult = { success: true };
    if (validationResult.success) {
      if (location.pathname === '/project/create') {
        await handleCreateDraftPost({
          title: projectEditorData.title,
          description: projectEditorData.description,
          category: projectEditorData.category,
          difficulty: projectEditorData.difficulty,
          tags: projectEditorData.tags,
          thumbnail: projectEditorData.fileUploadQueue.find(
            (file) => file.category === IAttachmentCategory.postThumbnail
          ),
        });
      } else if (postId) {
        await updatePostData({
          postId,
          data: {
            ...convertToPostDetailData(projectEditorData),
            currentPage: currentStep + 1,
          },
        });

        await uploadQueuedFiles();

        setErrors({});
        if (nextPage.condition) {
          const pageStep = Math.min(currentStep + 1, pages.length);
          setCurrentStep(pageStep);
          appendSearchParam('page', String(pageStep), true);
        } else {
          const pageStep = Math.min(currentStep + 2, pages.length);
          setCurrentStep(pageStep);
          appendSearchParam('page', String(pageStep), true);
        }
      } else {
        toast.error('Please fix the errors before continuing.');
      }
    }
  };

  const handleStepClick = async (targetStep: number) => {
    // Don't allow navigation to the same step
    if (targetStep === currentStep) {
      return;
    }

    // If navigating backwards, allow without validation
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
      appendSearchParam('page', String(targetStep), true);
      return;
    }

    // If navigating forwards, validate current step first
    const validationResult = await validateStep(
      currentStep,
      pages as ICarouselPage[],
      projectEditorData,
      setErrors
    );

    if (!validationResult.success) {
      toast.error('Please fix the validation errors before proceeding.');
      return;
    }

    // Save the current page data before navigating
    if (postId) {
      try {
        await updatePostData({
          postId,
          data: {
            ...convertToPostDetailData(projectEditorData),
            currentPage: targetStep,
          },
        });

        await uploadQueuedFiles();

        // Clear errors and navigate to target step
        setErrors({});
        setCurrentStep(targetStep);
        appendSearchParam('page', String(targetStep), true);
        toast.success('Progress saved successfully');
      } catch (error) {
        toast.error('Failed to save progress. Please try again.');
      }
    } else if (location.pathname === '/project/create') {
      // For new projects, don't allow skipping ahead before creating draft
      toast.error('Please complete the current step first.');
    } else {
      // Fallback: just navigate
      setCurrentStep(targetStep);
      appendSearchParam('page', String(targetStep), true);
    }
  };

  useEffect(() => {
    const pageStep = getSearchParam('page');
    if (!pageStep) return;
    setCurrentStep(parseInt(pageStep, 10));
  }, [getSearchParam]);

  const updateFormData = useCallback(async () => {
    if (!postId) return;
    dispatch(setProjectEditorData({ ...projectEditorData, isLoading: true }));
    const { data } = await getPostByIdQuery(postId);
    const currentPage = getSearchParam('page');
    if (!currentPage) return;
    if (!data) return;

    const postFormData = await convertToPostUploadData(data);
    dispatch(setProjectEditorData({ ...postFormData, isLoading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, getPostByIdQuery, getSearchParam, postId]);

  useEffect(() => {
    if (!postId) return;
    if (isUpdatePostDataLoading) return;
    // Only update form data when the component first loads or when navigating to a new step
    // Don't refetch after successful updates to avoid overwriting user changes
    if (updatePostDataStatus === QueryStatus.fulfilled) return;

    updateFormData();
  }, [
    currentStep,
    postId,
    isUpdatePostDataLoading,
    updatePostDataStatus,
    updateFormData,
  ]);

  if (
    projectEditorData?.isLoading ||
    isUploadingFiles ||
    isCreateDraftPostLoading ||
    isGetPostByIdLoading
  ) {
    return <Loader isLoading />;
  }

  const currentPageInfo = getCurrentPageInfo();
  const showSaveChanges = isEditMode && currentStep < pages.length;
  const showNext = currentStep < pages.length;
  // The last input page (License Details) submits for approval instead of
  // advancing — its primary button becomes "Publish" rather than "Next".
  const isPublishStep = currentStep === pages.length - 1;
  const isPostStatusPage = currentStep === pages.length;
  const showCustomAction = isPostStatusPage && !!postId;
  const showClose = currentStep < pages.length && !isPostStatusPage;

  const handleViewPostDetails = () => {
    if (postId) {
      navigate(`/model/${postId}`);
    }
  };

  const handleClose = () => {
    if (currentUser.user?.username) {
      navigate(`/${currentUser.user.username}`);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      <CarouselHeader
        disabled={isCreateDraftPostLoading || isSaving}
        title={currentPageInfo.title}
        description={currentPageInfo.description}
        showSaveChanges={showSaveChanges}
        showNext={showNext}
        showCustomAction={showCustomAction}
        customActionLabel="View Post Details"
        showClose={showClose}
        onSaveChanges={handleSaveChanges}
        onNext={isPublishStep ? handlePublish : handleNext}
        nextLabel={isPublishStep ? 'Publish' : 'Next'}
        onBack={handleBack}
        onCustomAction={handleViewPostDetails}
        onClose={handleClose}
        isSaving={isSaving}
        isNextLoading={isPublishStep ? isPublishing : isUpdatePostDataLoading}
      />

      <ProgressBar
        currentStep={currentStep}
        totalSteps={pages.length}
        onStepClick={handleStepClick}
        disabled={
          isCreateDraftPostLoading ||
          isSaving ||
          isUpdatePostDataLoading ||
          isUploadingFiles
        }
      />

      {/* Content area with full space utilization */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-full h-full p-4 bg-gray-50">
          <div
            className={cn(
              'w-full h-full',
              currentStep === 3 ? 'max-w-8xl mx-auto' : 'max-w-3xl mx-auto'
            )}
          >
            {pages[currentStep - 1].page}
          </div>
        </div>
      </div>
    </div>
  );
}
